import { AuditTrail } from '@card-software/nexus-models';
import { Translation } from '../translations';

/**
 * Hierarchical warehouse location management entity supporting 8-level deep location trees.
 * Represents physical storage locations from Site level down to individual Bins.
 *
 * Business Rules: Only leaf nodes (IsStorable=true) can hold physical inventory.
 * LocationCode is immutable once created for barcode stability.
 */
export interface LocationNode extends AuditTrail {
  id: string;
  type: LocationType;
  code: string;
  name: Translation;
  description: Translation;

  /**
   * Reference to parent LocationNode. Null indicates a top-level Site.
   *
   * Constraint: Cannot reference itself or create circular hierarchies.
   */
  parentId?: string;

  /**
   * Immutable global path identifier built from hierarchy traversal.
   * Used for barcode scanning and unique location identification.
   *
   * Format: "SITE-WAREHOUSE-ZONE-AISLE-RACK-BAY-LEVEL-BIN"
   * Auto-generated on save, never manually edited.
   */
  locationCode: string;

  /**
   * Indicates if this location can physically store inventory items.
   * Only leaf nodes (typically Bins) should be storable.
   *
   * Business Rule: Prevents accidentally assigning stock to high-level locations.
   */
  isStorable: boolean;

  /**
   * Optional SKU restriction - if set, only this ItemCode can be stored here.
   * Used for dedicated locations (hazmat, temperature-controlled, etc.).
   *
   * Null = accepts any item type. Validated during inventory movements.
   */
  itemCodeStored?: string;

  maxUnits?: number;

  /**
   * Denormalized location hierarchy for efficient querying and filtering.
   * Automatically populated from LocationCode during save operations.
   *
   * Performance Optimization: Avoids recursive parent traversal in queries.
   * Used for hierarchical queries, reporting aggregations, search filters.
   */
  path?: LocationPath;

  /**
   * Full hierarchy as a single name, e.g. "Northern Textiles > Raw Material Warehouse > A".
   * Populated only when the location endpoint is called with detailed=true.
   */
  fullName?: Translation;

  /**
   * Ordered ancestor names from Site down to this location. Populated only with detailed=true.
   */
  pathNames?: Translation[];
}

/**
 * Location codes addressed by name, mirroring `Inventory.UI`'s enum of the same name — the API resolves a
 * warehouse only by code, never by role. Unprefixed members are Northern Textiles; other sites carry the
 * prefix in the name (`PC_RAW_MATERIAL`, `SIMTEX_RAW_MATERIAL` there), so `RAW_MATERIAL` stays pinned to NT
 * whatever the site selector says.
 */
export enum ImportantLocations {
  RAW_MATERIAL = 'NT.WH-RM-1',
  PC_RAW_MATERIAL = 'PC.WH-RM-1',
  PC_FINISHED_GOODS = 'PC.WH-FG-1',
  GREIGE = 'NT.WH-GR-1',
  KNITTING = 'NT.WA-KNITTING-1',
  IN_ROUTE = 'NT.IN-ROUTE',
  SIMTEX_RAW_MATERIAL = 'SIMTEX.WH-RM-2',
  INITIAL_VIRTUAL = 'NT.INITIAL-WH-RM-1',
  SAMPLES = 'NT.WH-SM-1',
}

/**
 * Hierarchical level types for warehouse location organization.
 * Defines the 8-level location hierarchy from Site down to individual storage Bins.
 */
export enum LocationType {
  Site = 0,
  Warehouse = 1,
  Zone = 2,
  Aisle = 3,
  Rack = 4,
  Bay = 5,
  Level = 6,
  Bin = 7,
}

/**
 * Denormalized location hierarchy components for efficient querying.
 * Each field contains the Code value from the corresponding hierarchy level.
 *
 * All fields nullable to support partial hierarchy paths.
 * Example: Zone-level location has siteCode+warehouseCode+zoneCode populated, others null.
 */
export interface LocationPath {
  siteCode?: string;
  warehouseCode?: string;
  zoneCode?: string;
  aisleCode?: string;
  rackCode?: string;
  bayCode?: string;
  levelCode?: string;
  binCode?: string;
}

/**
 * Type for creating new location nodes (id is optional during creation)
 */
export type CreateLocationNode = Omit<LocationNode, 'id'> & {
  id?: undefined;
};

/**
 * Utility function to build a LocationPath object from a locationCode string.
 * Each segment is optional and will be undefined if not present in the code.
 */
export function BuildPathFromCode(locationCode: string): LocationPath {
  const segments = locationCode.split('.');
  return {
    siteCode: segments[0] || undefined,
    warehouseCode: segments[1] || undefined,
    zoneCode: segments[2] || undefined,
    aisleCode: segments[3] || undefined,
    rackCode: segments[4] || undefined,
    bayCode: segments[5] || undefined,
    levelCode: segments[6] || undefined,
    binCode: segments[7] || undefined,
  };
}

export function GetLocationCodeFromPath(path: LocationPath): string {
  const segments = [
    path.siteCode || '',
    path.warehouseCode || '',
    path.zoneCode || '',
    path.aisleCode || '',
    path.rackCode || '',
    path.bayCode || '',
    path.levelCode || '',
    path.binCode || '',
  ];
  return segments.filter((seg) => seg).join('.');
}
